import React, { useState, useEffect } from 'react';
import { Loader2, RefreshCw, Users, BookOpen, User, ChevronRight, AlertTriangle, X, Upload, Eye } from 'lucide-react';
import api from '../../api';
import StudentMarksEntry from './StudentMarksEntry';
import MarksSummary from './MarksSummary';
import AttainmentReport from './AttainmentReport';
import BulkUploadModal from './BulkUploadModal';

// Utility to check if attainment data has entries
const hasCoPoData = (attainment) => {
  return attainment && attainment.coAttainment && Object.keys(attainment.coAttainment).length > 0;
};

// ============================================================
// UPDATED: MarkEntryDashboard with batchId support
// ============================================================
const MarkEntryDashboard = ({ 
  assignmentId, 
  classId, 
  subjectId, 
  academicYearId,
  batchId,          // ✅ NEW: batchId for lab filtering
  batchNumber,      // ✅ NEW: batchNumber for lab filtering
  onClose,          // Optional - for modal mode
  isModal = false,  // Optional - indicates if rendered in modal
  readOnly = false, // Read-only mode for frozen components with marks
  isEndExam = false // Flag to indicate End Exam mode
}) => {
  const [activeTab, setActiveTab] = useState('entry');
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [assignment, setAssignment] = useState(null);
  const [questionStructure, setQuestionStructure] = useState([]);
  const [totalMaxMarks, setTotalMaxMarks] = useState(0);
  const [showMobileList, setShowMobileList] = useState(true);
  const [cieConfig, setCieConfig] = useState(null);
  const [currentComponentName, setCurrentComponentName] = useState(null);
  const [attainment, setAttainment] = useState(null);
  const [attainmentLoading, setAttainmentLoading] = useState(true);
  const [showBulkUpload, setShowBulkUpload] = useState(false);

  // Log batchId for debugging
  console.log('[MarkEntryDashboard] 🔍 Received batchId:', batchId);
  console.log('[MarkEntryDashboard] 🔍 Received batchNumber:', batchNumber);

  // Determine API endpoints based on type
  const marksApiEndpoint = isEndExam ? '/api/faculty/end-exam-marks' : '/api/faculty/marks-entry';
  const attainmentApiEndpoint = isEndExam 
    ? `/api/faculty/end-exam-paper-templates/${assignmentId}/analytics`
    : `/api/faculty/marks-entry/${assignmentId}/attainment`;

  useEffect(() => {
    if (assignmentId) {
      loadDashboard();
      if (!isEndExam) {
        // End Exam doesn't have CIE config or CO/PO attainment typically
        loadCieConfig();
        loadAttainment();
      }
    }
    // eslint-disable-next-line
  }, [assignmentId, isEndExam]);

  const loadAttainment = async () => {
    if (isEndExam) return; // Skip attainment for End Exam
    setAttainmentLoading(true);
    try {
      const res = await api.get(attainmentApiEndpoint);
      setAttainment(res.data.data);
    } catch (error) {
      setAttainment(null);
    } finally {
      setAttainmentLoading(false);
    }
  };

  const loadCieConfig = async () => {
    if (isEndExam) return; // Skip CIE config for End Exam
    try {
      const assignmentRes = await api.get(`/api/faculty/paper-template-assignment/${assignmentId}`);
      if (assignmentRes.data.success && assignmentRes.data.assignment) {
        const assignmentData = assignmentRes.data.assignment;
        const cieRes = await api.get('/api/faculty/cie-template/by-component', {
          params: { componentId: assignmentData.componentId }
        });
        if (cieRes.data.success) {
          setCieConfig(cieRes.data.data);
          const component = cieRes.data.data.cie?.components?.find(
            c => c.componentId === assignmentData.componentId
          );
          if (component) {
            setCurrentComponentName(component.name);
          }
        }
      }
    } catch (error) {
      console.error('Error loading CIE config:', error);
    }
  };

  // ============================================================
  // UPDATED: loadDashboard with batchId support
  // ============================================================
  const loadDashboard = async () => {
    setLoading(true);
    setError(null);
    try {
      // Build URL with batchId if provided
      let url = `${marksApiEndpoint}/${assignmentId}/students`;
      const params = new URLSearchParams();
      
      if (batchId) {
        params.append('batchId', batchId);
        console.log(`[MarkEntryDashboard] 🎯 Filtering by batchId: ${batchId}`);
      }
      if (batchNumber) {
        params.append('batchNumber', batchNumber);
        console.log(`[MarkEntryDashboard] 🎯 Filtering by batchNumber: ${batchNumber}`);
      }
      
      const queryString = params.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
      
      console.log(`[MarkEntryDashboard] 📡 Fetching from URL: ${url}`);
      
      const response = await api.get(url);
      
      if (response.data.success) {
        const data = response.data.data;
        const fetchedStudents = data.students || [];
        console.log(`[MarkEntryDashboard] ✅ Found ${fetchedStudents.length} students`);
        
        setStudents(fetchedStudents);
        setQuestionStructure(data.questionStructure || []);
        setTotalMaxMarks(data.totalMaxMarks || 0);
        setSummary({
          summary: data.summary,
          students: fetchedStudents,
          assignment: data.assignment
        });
        setAssignment(data.assignment);
        
        if (fetchedStudents.length > 0 && !selectedStudent) {
          setSelectedStudent(fetchedStudents[0]);
        } else if (selectedStudent) {
          const updated = fetchedStudents.find(s => s.studentId === selectedStudent.studentId);
          if (updated) setSelectedStudent(updated);
        }
      }
    } catch (error) {
      console.error('Error loading dashboard:', error);
      setError(error.response?.data?.message || 'Failed to sync resource state.');
    } finally {
      setLoading(false);
    }
  };

  const rebuildSummary = (studentList) => {
    const totalStudents = studentList.length;
    const averagePercentage = totalStudents > 0
      ? studentList.reduce((sum, student) => sum + (student.percentage || 0), 0) / totalStudents
      : 0;

    return {
      totalStudents,
      averagePercentage,
      gradeDistribution: studentList.reduce((distribution, student) => {
        const grade = student.grade || 'F';
        if (distribution[grade] !== undefined) {
          distribution[grade] += 1;
        }
        return distribution;
      }, {
        O: 0,
        'A+': 0,
        A: 0,
        'B+': 0,
        B: 0,
        C: 0,
        P: 0,
        F: 0,
        ABSENT: 0
      })
    };
  };

  const handleStudentSaved = (updatedStudent) => {
    if (!updatedStudent?.studentId) return;

    setStudents(prevStudents => {
      const nextStudents = prevStudents.map(student => (
        student.studentId === updatedStudent.studentId
          ? { ...student, ...updatedStudent }
          : student
      ));

      setSummary(prevSummary => {
        if (!prevSummary) return prevSummary;
        return {
          ...prevSummary,
          students: nextStudents,
          summary: rebuildSummary(nextStudents)
        };
      });

      setSelectedStudent(prevSelected => {
        if (!prevSelected || prevSelected.studentId !== updatedStudent.studentId) {
          return prevSelected;
        }
        return { ...prevSelected, ...updatedStudent };
      });

      return nextStudents;
    });
  };

  const refreshAfterBulkUpload = () => {
    loadDashboard();
    if (!isEndExam) loadAttainment();
  };

  const selectStudentOnMobile = (student) => {
    setSelectedStudent(student);
    setShowMobileList(false);
  };

  const ciePercentage = cieConfig?.finalGradeCombination?.ciePercentage || 40;
  const summaryTabLabel = isEndExam ? 'End Exam Summary' : (cieConfig?.name ? `${cieConfig.name} Summary` : 'CIE Summary');

  // Get tenant ID from localStorage or context
  const getTenantId = () => {
    return localStorage.getItem('tenantId') || '';
  };

  if (loading) {
    return (
      <div className="w-full bg-white rounded-2xl border border-gray-100 p-16 text-center shadow-xs">
        <Loader2 className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-3" />
        <p className="text-gray-500 font-medium text-sm">Assembling context workspace...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 p-8 text-center max-w-lg mx-auto shadow-sm">
        <div className="w-12 h-12 bg-rose-50 rounded-full flex items-center justify-center mx-auto mb-4 border border-rose-100">
          <AlertTriangle className="w-6 h-6 text-rose-600" />
        </div>
        <h3 className="text-base font-bold text-gray-900 mb-1">Synchronization Fault</h3>
        <p className="text-xs text-gray-500 mb-5 leading-relaxed">{error}</p>
        <button
          onClick={loadDashboard}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white font-semibold text-xs rounded-xl hover:bg-blue-700 transition-all shadow-md shadow-blue-600/10 cursor-pointer"
        >
          <RefreshCw className="w-3.5 h-3.5" /> Re-Fetch Records
        </button>
      </div>
    );
  }

  if (students.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center max-w-md mx-auto shadow-sm">
        <Users className="w-10 h-10 text-gray-300 mx-auto mb-3" />
        <h3 className="text-base font-bold text-gray-900 mb-1">Roster Is Empty</h3>
        <p className="text-xs text-gray-500 leading-relaxed">
          No registered student profiles were located for the parameters provided.
          {batchId && (
            <span className="block text-purple-600 mt-1">
              Filtered by batch: {batchId}
            </span>
          )}
          {batchNumber && (
            <span className="block text-purple-600 mt-1">
              Batch Number: {batchNumber}
            </span>
          )}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6 max-w-7xl mx-auto px-1 font-sans antialiased">
      
      {/* Close button for modal mode */}
      {isModal && onClose && (
        <div className="flex justify-end">
          <button
            onClick={onClose}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition cursor-pointer"
          >
            <X className="w-3.5 h-3.5" /> Close
          </button>
        </div>
      )}
      
      {/* Upper Meta-Information Strip */}
      <div className="bg-white rounded-xl border border-gray-200/80 p-2 sm:p-4 shadow-xs">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 sm:gap-4">
          <div className="flex items-start gap-2 sm:gap-3">
            <div className="p-2 bg-blue-50 text-blue-600 rounded-xl border border-blue-100 shrink-0 hidden sm:block">
              <BookOpen className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-sm sm:text-base md:text-lg font-black text-gray-900 leading-tight">
                  {assignment?.subjectName}
                </h2>
                {readOnly && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 text-slate-600 rounded-md text-[10px] font-bold uppercase tracking-wider">
                    <Eye className="w-3 h-3" /> Read Only
                  </span>
                )}
                {isEndExam && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-700 rounded-md text-[10px] font-bold uppercase tracking-wider">
                    End Exam
                  </span>
                )}

                {batchNumber && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-700 rounded-md text-[10px] font-bold uppercase tracking-wider">
                    Batch #{batchNumber}
                  </span>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-x-2 sm:gap-x-3 gap-y-1 mt-1 text-[11px] sm:text-xs text-gray-500 font-medium">
                <span className="font-mono bg-gray-100 px-1 py-0.5 sm:px-1.5 rounded text-gray-600">{assignment?.subjectCode}</span>
                <span className="hidden md:inline">•</span>
                <span>Active Roll Count: <b>{students.length}</b></span>
                <span>•</span>
                <span>Max Target: <b>{totalMaxMarks} M</b></span>
                {!isEndExam && <span>•</span>}
                {!isEndExam && <span>{cieConfig?.name?.split(' ')[0] || 'CIE'} Weightage: <b>{ciePercentage}%</b></span>}
                {batchId && (
                  <span className="text-purple-600 font-mono text-[10px]">
                  </span>
                )}
              </div>
            </div>
          </div>
          
          {/* Action Buttons - Hidden in read-only mode */}
          {!readOnly && (
            <div className="flex items-center gap-1 sm:gap-2 mt-2 sm:mt-0">
              <button
                onClick={() => setShowBulkUpload(true)}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 sm:px-3.5 sm:py-2 text-[11px] sm:text-xs font-bold bg-green-50 hover:bg-green-100 text-green-700 border border-green-200 rounded-xl transition-all shadow-2xs cursor-pointer"
              >
                <Upload className="w-3.5 h-3.5" /> Bulk Upload
              </button>
              <button
                onClick={loadDashboard}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 sm:px-3.5 sm:py-2 text-[11px] sm:text-xs font-bold bg-slate-50 hover:bg-slate-100 text-gray-700 border border-gray-200 rounded-xl transition-all shadow-2xs cursor-pointer"
              >
                <RefreshCw className="w-3.5 h-3.5" /> Synchronize
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Touch-Optimized Segment Navigation */}
      <div className="border-b border-gray-200 overflow-x-auto scrollbar-none flex-nowrap whitespace-nowrap">
        <nav className="flex space-x-6 md:space-x-8 px-1">
          <button
            className={`py-3 px-1 border-b-2 font-bold text-xs md:text-sm tracking-wide transition-all cursor-pointer ${
              activeTab === 'entry'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-400 hover:text-gray-700'
            }`}
            onClick={() => {
              setActiveTab('entry');
              setShowMobileList(true);
            }}
          >
            Marks Entry Matrix
          </button>
          <button
            className={`py-3 px-1 border-b-2 font-bold text-xs md:text-sm tracking-wide transition-all cursor-pointer ${
              activeTab === 'summary'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-400 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('summary')}
          >
            {summaryTabLabel}
          </button>
          {!isEndExam && (!attainmentLoading && hasCoPoData(attainment)) && (
            <button
              className={`py-3 px-1 border-b-2 font-bold text-xs md:text-sm tracking-wide transition-all cursor-pointer ${
                activeTab === 'attainment'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-400 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('attainment')}
            >
              Attainment (CO-PO)
            </button>
          )}
        </nav>
      </div>

      {/* Contextual Screen Panels */}
      <div className="focus:outline-none">
        {activeTab === 'entry' && (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 md:gap-6 items-start">
            
            {/* Sidebar List Selector */}
            <div className={`lg:col-span-1 bg-white border border-gray-200 rounded-xl shadow-xs overflow-hidden ${
              !showMobileList ? 'hidden lg:block' : 'block'
            }`}>
              <div className="p-4 border-b border-gray-100 bg-slate-50/70">
                <h3 className="font-bold text-xs text-gray-800 uppercase tracking-wider flex items-center gap-1.5">
                  <Users className="w-4 h-4 text-gray-400" /> Allocation Ledger ({students.length})
                </h3>

              </div>
              <div className="max-h-[500px] lg:max-h-[600px] overflow-y-auto divide-y divide-gray-100">
                {students.map((student, idx) => {
                  const isSelected = selectedStudent?.studentId === student.studentId;
                  const hasMarks = student.totalMarksObtained > 0;
                  
                  return (
                    <button
                      key={student.studentId || idx}
                      onClick={() => selectStudentOnMobile(student)}
                      className={`w-full text-left p-3.5 flex items-center justify-between gap-3 transition-all cursor-pointer ${
                        isSelected
                          ? 'bg-blue-50/60 border-l-4 border-l-blue-600'
                          : 'hover:bg-slate-50/50'
                      }`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className={`text-sm font-bold truncate ${isSelected ? 'text-blue-700' : 'text-gray-900'}`}>
                          {student.studentName}
                        </div>
                        <div className="text-[11px] font-mono tracking-wide text-gray-400 mt-0.5">{student.studentUSN}</div>
                        
                        {hasMarks && (
                          <div className="inline-flex items-center gap-1 text-[10px] font-extrabold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded mt-1.5 border border-emerald-100">
                            Scored: {student.percentage?.toFixed(0)}% ({student.totalMarksObtained}/{student.totalMaxMarks})
                          </div>
                        )}
                      </div>
                      <ChevronRight className={`w-4 h-4 shrink-0 transition-transform ${
                        isSelected ? 'text-blue-500 translate-x-0.5' : 'text-gray-300'
                      }`} />
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Assessment Input Matrix Container */}
            <div className={`lg:col-span-3 ${showMobileList ? 'hidden lg:block' : 'block'}`}>
              {selectedStudent ? (
                <div className="space-y-4">
                  <div className="block lg:hidden">
                    <button
                      onClick={() => setShowMobileList(true)}
                      className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-white border border-gray-300 hover:bg-slate-50 text-gray-700 font-bold text-xs rounded-xl shadow-2xs transition-colors cursor-pointer"
                    >
                      ← Back to Student Ledger List
                    </button>
                  </div>
                  
                  <StudentMarksEntry
                    assignmentId={assignmentId}
                    student={selectedStudent}
                    questionStructure={questionStructure}
                    totalMaxMarks={totalMaxMarks}
                    isSimple={questionStructure?.length === 0}
                    componentName={currentComponentName}
                    onSave={handleStudentSaved}
                    readOnly={readOnly}
                    isEndExam={isEndExam}
                  />
                </div>
              ) : (
                <div className="bg-white border border-gray-200 rounded-xl p-12 text-center text-gray-400 font-medium shadow-2xs">
                  <User className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                  Select an evaluation profile record from the tracking sheet list to map marks scores.
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'summary' && (
          <div className="bg-white border border-gray-200 rounded-xl p-1 md:p-4 shadow-xs">
            <MarksSummary 
              assignmentId={assignmentId} 
              summary={summary}
              classId={classId}
              subjectId={subjectId}
              academicYearId={academicYearId}
              componentName={currentComponentName}
              isEndExam={isEndExam}
            />
          </div>
        )}

        {activeTab === 'attainment' && !isEndExam && (!attainmentLoading && hasCoPoData(attainment)) && (
          <div className="bg-white border border-gray-200 rounded-xl p-1 md:p-4 shadow-xs">
            <AttainmentReport assignmentId={assignmentId} />
          </div>
        )}
      </div>

      {/* Bulk Upload Modal - Only shown when not in read-only mode */}
      {!readOnly && showBulkUpload && (
        <BulkUploadModal
          assignmentId={assignmentId}
          onClose={() => setShowBulkUpload(false)}
          onSuccess={refreshAfterBulkUpload}
          tenantId={getTenantId()}
          isEndExam={isEndExam}
          batchId={batchId}           
          batchNumber={batchNumber}   
        />
      )}
    </div>
  );
};

export default MarkEntryDashboard;